/**
 * 暖棕摩卡模板 - 单页
 *
 * 设计理念：深可可棕页头条 + 焦糖装饰 + 圆角正文卡
 * 与 executive（深墨绿方角卡）完全不同：
 * - 页头：深棕渐变 + 焦糖圆点装饰
 * - 正文：白色圆角卡片 + 焦糖引用链接
 * - 面包屑 + 内容区居中阅读
 */

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import MochaNavbar from "@/templates/mocha/components/Navbar";
import MochaFooter from "@/templates/mocha/components/Footer";
import { getSinglePage } from "@/utils/api";

export default function MochaSinglePage() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    getSinglePage(slug)
      .then((data) => setPage(data))
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <LoadingSkeleton />;
  if (!page) return <NotFound />;

  return (
    <div className="min-h-screen bg-[#FAF6F0]">
      <MochaNavbar />

      {/* ── 页面头图区域：深棕 + 焦糖装饰 ── */}
      <section className="relative bg-gradient-to-br from-[#4A3525] via-[#3F2C1F] to-[#2B1D14] py-14 md:py-16 overflow-hidden">
        {/* 顶部焦糖细线 */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#C77B3F] via-[#DCA96B] to-[#C77B3F]" />
        {/* 焦糖圆环装饰 */}
        <div className="absolute -top-20 right-16 w-64 h-64 rounded-full border border-[#C77B3F]/15" />
        <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full border border-[#C77B3F]/10" />
        <div className="absolute top-10 left-1/3 w-56 h-56 bg-[#C77B3F]/10 rounded-full blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-4">
            <span className="w-1.5 h-10 bg-[#C77B3F] rounded-full shrink-0" />
            <h1 className="text-2xl md:text-3xl font-black text-[#F5EBDD]">
              {page.title}
            </h1>
          </div>
          {/* 装饰横线 */}
          <div className="mt-4 ml-6 flex items-center gap-2">
            <span className="w-10 h-0.5 bg-[#C77B3F]/60 rounded-full" />
            <span className="w-2 h-2 bg-[#DCA96B] rounded-full" />
            <span className="w-10 h-0.5 bg-[#C77B3F]/60 rounded-full" />
          </div>
        </div>
      </section>

      {/* ── 面包屑 ── */}
      <div className="bg-white border-b border-[#F0E6D6]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-3 flex items-center gap-1.5 text-sm">
          <Link to="/" className="text-gray-400 hover:text-[#C77B3F] transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-xs text-gray-300" />
          <span className="text-[#B0672F] font-semibold">{page.title}</span>
        </div>
      </div>

      {/* ── 内容区 ── */}
      <article className="py-10 md:py-14">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="bg-white rounded-xl p-6 md:p-10 border border-[#F0E6D6] shadow-sm">
            {page.content ? (
              <div
                className="prose prose-lg max-w-none prose-headings:text-[#4A3525] prose-p:text-gray-600 prose-p:leading-loose prose-a:text-[#A8622A] prose-a:no-underline hover:prose-a:text-[#C77B3F] prose-strong:text-[#4A3525] prose-img:rounded-lg prose-img:border prose-img:border-[#F0E6D6]"
                dangerouslySetInnerHTML={{ __html: page.content }}
              />
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-lg bg-[#C77B3F]/10 border border-[#C77B3F]/20">
                  <i className="ri-file-text-line text-2xl text-[#C77B3F]" />
                </div>
                <p className="text-gray-400">暂无内容</p>
              </div>
            )}
          </div>

          {/* 返回首页按钮 */}
          <div className="mt-8 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-[#C77B3F] transition-colors group"
            >
              <i className="ri-arrow-left-line group-hover:-translate-x-1 transition-transform" />
              返回首页
            </Link>
          </div>
        </div>
      </article>

      <MochaFooter />
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-[#FAF6F0]">
      <MochaNavbar />
      <section className="bg-gradient-to-br from-[#4A3525] to-[#2B1D14] py-16">
        <div className="max-w-4xl mx-auto px-6 text-center animate-pulse">
          <div className="w-48 h-8 bg-white/20 rounded-md mx-auto" />
          <div className="flex items-center justify-center gap-2 mt-4">
            <div className="w-8 h-0.5 bg-white/20 rounded-full" />
            <div className="w-2 h-2 bg-white/20 rounded-full" />
            <div className="w-8 h-0.5 bg-white/20 rounded-full" />
          </div>
        </div>
      </section>
      <div className="py-12 max-w-3xl mx-auto px-6">
        <div className="animate-pulse space-y-4 p-10 bg-white rounded-xl border border-[#F0E6D6]">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className={`bg-gray-200 rounded ${i === 0 ? "h-7 w-2/3" : i === 5 ? "h-4 w-1/3" : "h-4 w-full"}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center">
      <div className="text-center px-4">
        <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-lg bg-[#C77B3F]/10 border border-[#C77B3F]/20">
          <i className="ri-file-damage-line text-3xl text-[#C77B3F]" />
        </div>
        <h1 className="text-xl font-bold text-[#4A3525] mb-2">页面未找到</h1>
        <p className="text-sm text-gray-500 mb-6">您访问的单页不存在或已被删除</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#C77B3F] to-[#A8622A] text-white text-sm font-bold rounded-lg hover:from-[#B0672F] hover:to-[#8F5422] transition-all"
        >
          <i className="ri-home-4-line" />
          返回首页
        </Link>
      </div>
    </div>
  );
}
